import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  useEditMaintenanceDetailsMutation,
  useOrganizationMaintenanceQuery,
  useSingleOrganizationMaintenanceQuery,
} from "../../../api/network/maintenanceApiService";
import {
  setListingQueryParams,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import {
  MaintenanceFaultCodeResponse,
  TLegacyMaintenance,
  TSingleLegacyMaintenance,
} from "../../../api/types/Maintenance";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../../api/types/User";
import svgIcons from "../../../assets/svg/svgIcons";
import Accordian from "../../../components/accordian";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { routeUrls } from "../../../navigation/routeUrls";
import {
  dateToGregorianDays,
  gregorianDaysToJsDate,
} from "../../../utils/dateTime";
import { getDistanceByUnit, getDistanceInKms } from "../../../utils/distance";
import { useLoggedInUserData } from "../../../utils/user";
import { DISTANCE_UNIT_VALUES } from "../account/constants";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import AddMaintenanceDetails from "./components/addMaintenanceDetails";
import {
  MaintenanceIntervalDetailForm,
  MaintenanceLastMaintEngineHoursDetailForm,
  MaintenanceLastPerodicServiceDateForm,
  MaintenanceOdometerDetailForm,
} from "./maintenance-form";
import {
  maintenanceDetailsInitialValues,
  maintenanceDetailsYupValidationSchema,
  TFormFieldNames,
} from "./validation";
import { useSingleEnterpriseQuery } from "../../../api/network/sysadminApiServices";
import FormActionButtons from "../../../components/admin/formActionButtons";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import ModalMaintenanceFaultCodes from "./components/modalMaintenanceFaultCodes";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";
import { useSingleOrganizationVehicleQuery } from "../../../api/network/adminApiServices";

const ScreenAdminDetailMaintenance = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const { t: tMain } = useTranslation();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "admins.maintenance",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.maintenance.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();

  const { getLocalStorageItem } = useLocalStorage();
  const thisOrgDriverGroupAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverGroupAllowed === "1";
  // get account details from session storage
  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
  );
  const loggedInUser: TLegacyUserWithAccount =
    getLocalStorageItem(localStorageKeys.user) ?? loggedInUserData?.user;
  const { account } = loggedInUser;
  const distanceUnits = account.distanceunits;

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { maintenance: orgMaintenanceQueryParams } = listingQueryParams;

  // flag to idenfiy if user is coming from create new user popup
  const isNewEntity = React.useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(
    !!isNewEntity?.current
  );

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        maintenance: { ...orgMaintenanceQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  // fetch organization maintenance
  const {
    data: dataOrgMaintenance,
    isFetching: isFetchingOrgMaintenance,
    error,
  } = useOrganizationMaintenanceQuery(orgMaintenanceQueryParams);
  const { results, count } = dataOrgMaintenance || {};

  // fetch single maintenance details
  const {
    data: dataSingleMaintenance,
    isFetching: isFetchingSingleMaintenance,
  } = useSingleOrganizationMaintenanceQuery(
    { accountid: thisUserOrganizationId, deviceid: deviceId },
    { skip: !deviceId }
  );

  // fetch single vehicle details
  const {
    data: dataSingleVehicle,
    isFetching: isFetchingSingleVehicle,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useSingleOrganizationVehicleQuery(
    {
      account_id: thisUserOrganizationId,
      device_id: deviceId? deviceId : "",
    },
    {
      skip: !deviceId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );

  // maintenance mutations
  const [editMaintenanceApiTrigger, { isLoading: isLoadingEditMaintenance }] =
    useEditMaintenanceDetailsMutation();
  const [faultCodeData, setFaultCodeData] =
    useState<MaintenanceFaultCodeResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  useEffect(() => {
    setFormikValuesReady(!isFetchingSingleMaintenance);
  }, [isFetchingSingleMaintenance]);

  const formik = useFormik({
    initialValues: maintenanceDetailsInitialValues,
    validationSchema: maintenanceDetailsYupValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      const preparedMaintenanceEditParam: Partial<TSingleLegacyMaintenance> = {
        deviceid: deviceId,
        // lastodometerkm: values?.reported_odometer,
        lastodometerkm: getDistanceInKms(
          values?.reported_odometer - getDistanceByUnit(
            dataSingleVehicle?.odometeroffsetkm || 0,
            distanceUnits.toString() as DISTANCE_UNIT_VALUES
          ),
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        lastenginehours: values?.reported_engine_hours - (dataSingleVehicle?.enginehoursoffset || 0),
        notes: values?.notes,
        reminderinterval: values?.reminder_interval,
        remindermessage: values?.reminder_message,
        lastfaultcode: values?.fault_codes,
        // dcsconfigstring: values?.configuration_string, // disabled per client feedback

        maintodometerkm0: getDistanceInKms(
          values?.maintodometerkm0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm0: getDistanceInKms(
          values?.maintintervalkm0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintodometerkm1: getDistanceInKms(
          values?.maintodometerkm1,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm1: getDistanceInKms(
          values?.maintintervalkm1,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintodometerkm2: getDistanceInKms(
          values?.maintodometerkm2,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm2: getDistanceInKms(
          values?.maintintervalkm2,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintodometerkm3: getDistanceInKms(
          values?.maintodometerkm3,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm3: getDistanceInKms(
          values?.maintintervalkm3,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintodometerkm4: getDistanceInKms(
          values?.maintodometerkm4,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm4: getDistanceInKms(
          values?.maintintervalkm4,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintodometerkm5: getDistanceInKms(
          values?.maintodometerkm5,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm5: getDistanceInKms(
          values?.maintintervalkm5,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintodometerkm6: getDistanceInKms(
          values?.maintodometerkm6,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm6: getDistanceInKms(
          values?.maintintervalkm6,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintodometerkm7: getDistanceInKms(
          values?.maintodometerkm7,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm7: getDistanceInKms(
          values?.maintintervalkm7,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintodometerkm8: getDistanceInKms(
          values?.maintodometerkm8,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm8: getDistanceInKms(
          values?.maintintervalkm8,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintodometerkm9: getDistanceInKms(
          values?.maintodometerkm9,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm9: getDistanceInKms(
          values?.maintintervalkm9,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintenghourshr0: values?.maintenghourshr0,
        maintintervalhr0: values?.maintintervalhr0,
        maintenghourshr1: values?.maintenghourshr1,
        maintintervalhr1: values?.maintintervalhr1,
        maintenghourshr2: values?.maintenghourshr2,
        maintintervalhr2: values?.maintintervalhr2,
        maintenghourshr3: values?.maintenghourshr3,
        maintintervalhr3: values?.maintintervalhr3,
        maintenghourshr4: values?.maintenghourshr4,
        maintintervalhr4: values?.maintintervalhr4,

        maintfixedtimeft0: !!values?.maintfixedtimeft0
          ? dateToGregorianDays(new Date(values?.maintfixedtimeft0))
          : 0,
        maintintervalft0: values?.maintintervalft0,
        maintprenotifyft0: values?.maintprenotifyft0,
        maintfixedtimeft1: !!values?.maintfixedtimeft1
          ? dateToGregorianDays(new Date(values?.maintfixedtimeft1))
          : 0,
        maintintervalft1: values?.maintintervalft1,
        maintprenotifyft1: values?.maintprenotifyft1,
        maintfixedtimeft2: !!values?.maintfixedtimeft2
          ? dateToGregorianDays(new Date(values?.maintfixedtimeft2))
          : 0,
        maintintervalft2: values?.maintintervalft2,
        maintprenotifyft2: values?.maintprenotifyft2,
        maintfixedtimeft3: !!values?.maintfixedtimeft3
          ? dateToGregorianDays(new Date(values?.maintfixedtimeft3))
          : 0,
        maintintervalft3: values?.maintintervalft3,
        maintprenotifyft3: values?.maintprenotifyft3,
        maintfixedtimeft4: !!values?.maintfixedtimeft4
          ? dateToGregorianDays(new Date(values?.maintfixedtimeft4))
          : 0,
        maintintervalft4: values?.maintintervalft4,
        maintprenotifyft4: values?.maintprenotifyft4,
      };

      // call api for updating maintenance
      editMaintenanceApiTrigger(preparedMaintenanceEditParam)
        .unwrap()
        .then(() => {
          toast.success(t("toast.maintenance_updated"));
          navigate(routeUrls.dashboardChildren.maintenance);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (dataSingleMaintenance) {
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        // reported_odometer: dataSingleMaintenance?.lastodometerkm || 0,
        reported_odometer: getDistanceByUnit(
          dataSingleMaintenance?.lastodometerkm + (dataSingleVehicle?.odometeroffsetkm || 0) || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        reported_engine_hours: dataSingleMaintenance?.lastenginehours + (dataSingleVehicle?.enginehoursoffset || 0) || 0,
        notes: dataSingleMaintenance?.notes || "",
        reminder_interval: dataSingleMaintenance?.reminderinterval || "",
        reminder_message: dataSingleMaintenance?.remindermessage || "",
        fault_codes:
          dataSingleMaintenance?.lastfaultcode
            ?.replace(/^.*?dtc=/, "") // Remove up to and including 'dtc='
            ?.replace(/\s.*$/, "") // Remove from first space to end
            ?.replace(/,/g, ";") || "", // Replace commas with semicolon
        // configuration_string: dataSingleMaintenance?.dcsconfigstring || "", // disabled per client feedback

        maintodometerkm0: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm0 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm0: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm0 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintodometerkm1: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm1 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm1: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm1 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintodometerkm2: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm2 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm2: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm2 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintodometerkm3: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm3 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm3: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm3 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintodometerkm4: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm4 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm4: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm4 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintodometerkm5: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm5 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm5: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm5 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintodometerkm6: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm6 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm6: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm6 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintodometerkm7: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm7 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm7: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm7 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintodometerkm8: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm8 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm8: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm8 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintodometerkm9: getDistanceByUnit(
          dataSingleMaintenance?.maintodometerkm9 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        maintintervalkm9: getDistanceByUnit(
          dataSingleMaintenance?.maintintervalkm9 || 0,
          distanceUnits.toString() as DISTANCE_UNIT_VALUES
        ) as number,

        maintenghourshr0: dataSingleMaintenance?.maintenghourshr0 || 0,
        maintintervalhr0: dataSingleMaintenance?.maintintervalhr0 || 0,

        maintenghourshr1: dataSingleMaintenance?.maintenghourshr1 || 0,
        maintintervalhr1: dataSingleMaintenance?.maintintervalhr1 || 0,

        maintenghourshr2: dataSingleMaintenance?.maintenghourshr2 || 0,
        maintintervalhr2: dataSingleMaintenance?.maintintervalhr2 || 0,

        maintenghourshr3: dataSingleMaintenance?.maintenghourshr3 || 0,
        maintintervalhr3: dataSingleMaintenance?.maintintervalhr3 || 0,

        maintenghourshr4: dataSingleMaintenance?.maintenghourshr4 || 0,
        maintintervalhr4: dataSingleMaintenance?.maintintervalhr4 || 0,

        maintfixedtimeft0: dataSingleMaintenance?.maintfixedtimeft0
          ? gregorianDaysToJsDate(
              dataSingleMaintenance?.maintfixedtimeft0
            ).getTime()
          : 0,
        maintintervalft0: dataSingleMaintenance?.maintintervalft0 || 0,
        maintprenotifyft0: dataSingleMaintenance?.maintprenotifyft0 || 0,

        maintfixedtimeft1: dataSingleMaintenance?.maintfixedtimeft1
          ? gregorianDaysToJsDate(
              dataSingleMaintenance?.maintfixedtimeft1
            ).getTime()
          : 0,
        maintintervalft1: dataSingleMaintenance?.maintintervalft1 || 0,
        maintprenotifyft1: dataSingleMaintenance?.maintprenotifyft1 || 0,

        maintfixedtimeft2: dataSingleMaintenance?.maintfixedtimeft2
          ? gregorianDaysToJsDate(
              dataSingleMaintenance?.maintfixedtimeft2
            ).getTime()
          : 0,
        maintintervalft2: dataSingleMaintenance?.maintintervalft2 || 0,
        maintprenotifyft2: dataSingleMaintenance?.maintprenotifyft2 || 0,

        maintfixedtimeft3: dataSingleMaintenance?.maintfixedtimeft3
          ? gregorianDaysToJsDate(
              dataSingleMaintenance?.maintfixedtimeft3
            ).getTime()
          : 0,
        maintintervalft3: dataSingleMaintenance?.maintintervalft3 || 0,
        maintprenotifyft3: dataSingleMaintenance?.maintprenotifyft3 || 0,

        maintfixedtimeft4: dataSingleMaintenance?.maintfixedtimeft4
          ? gregorianDaysToJsDate(
              dataSingleMaintenance?.maintfixedtimeft4
            ).getTime()
          : 0,
        maintintervalft4: dataSingleMaintenance?.maintintervalft4 || 0,
        maintprenotifyft4: dataSingleMaintenance?.maintprenotifyft4 || 0,
      });
      setUserCanEdit(!!isNewEntity?.current);
      setTimeout(() => {
        setFormikValuesReady(true);
      }, 200); // simulate render delay for select pre-selected values
    }
  }, [deviceId, dataSingleMaintenance, isFetchingSingleMaintenance]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    dirty,
  } = formik;

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;

  // handle edit maintenance
  const handleEditMaintenance = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  const onBackButtonClick = () =>
    navigate(routeUrls.dashboardChildren.maintenance);

  // fetch single enterprise details
  const {
    data: dataSingleEnterprise,
    isFetching: isFetchingSingleEnterprise,
    isError: isErrorSingleEnterprise,
    error: errorSingleEnterprise,
  } = useSingleEnterpriseQuery(
    {
      enterprise_id: `${loggedInUser.accountid}`,
    },
    {
      skip: !loggedInUser.accountid,
      refetchOnMountOrArgChange: true,
    }
  );

  // Divide the flags into three categories
  const maintenanceFlagInterval = useMemo(() => {
    return Object.fromEntries(
      Object.entries(dataSingleEnterprise?.maintenance_flag || {}).filter(
        ([key]) => key.startsWith("maint_flag_km")
      )
    );
  }, [dataSingleEnterprise?.maintenance_flag]);

  const maintenanceFlagLastMaintEngine = useMemo(() => {
    return Object.fromEntries(
      Object.entries(dataSingleEnterprise?.maintenance_flag || {}).filter(
        ([key]) => key.startsWith("maint_flag_hr")
      )
    );
  }, [dataSingleEnterprise?.maintenance_flag]);

  const maintenanceFlagLastPeriodicService = useMemo(() => {
    return Object.fromEntries(
      Object.entries(dataSingleEnterprise?.maintenance_flag || {}).filter(
        ([key]) => key.startsWith("maint_flag_ft")
      )
    );
  }, [dataSingleEnterprise?.maintenance_flag]);
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "button",
              variant: "primary",
              label: userCanEdit ? tMain("update") : tMain("edit"),
              onClick: userCanEdit
                ? handleEditMaintenance
                : () => setUserCanEdit(!userCanEdit),
              disabled:
                isFetchingSingleMaintenance ||
                isLoadingEditMaintenance ||
                (userCanEdit && !!Object.keys(errors).length), //|| getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_MAINTENANCE) < ROLE_ACCESS_LEVELS.WRITE
            },
          ]}
        />
      </>
    );
  }, [
    modalsState,
    isLoadingEditMaintenance,
    isFetchingSingleMaintenance,
    userCanEdit,
    errors,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleEditMaintenance(),
              disabled:
                isLoadingEditMaintenance || !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [isLoadingEditMaintenance, errors]);
  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingOrgMaintenance ? " opacity-40 pointer-events-none" : ""}${isNewEntity?.current ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={orgMaintenanceQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {dataOrgMaintenance?.results?.map(
                (item: TLegacyMaintenance, index: number) => (
                  <AdminListingColumnItem
                    key={index}
                    selected={deviceId === item.deviceid}
                    onClick={() =>
                      navigate(
                        `${routeUrls.dashboardChildren.maintenance}/${item.deviceid}`
                      )
                    }
                    title={consistentDisplayAssetItem(
                      item.deviceid,
                      item.description,
                      item.displayname,
                      item.drivername,
                      showDriver
                    )}
                    description={item.displayname} // item.description
                    asideText={item.equipmenttype}
                    bottomText={""} // item.uniqueid}
                  />
                )
              )}
            </div>
            {!isFetchingOrgMaintenance && (
              <Pagination
                pageSize={orgMaintenanceQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      maintenance: {
                        ...orgMaintenanceQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count
                    ? Math.ceil(count / orgMaintenanceQueryParams.page_size)
                    : 1
                }
                forcePage={orgMaintenanceQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      maintenance: {
                        ...orgMaintenanceQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {isFetchingSingleMaintenance && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditMaintenance}
                      disabled={isLoadingEditMaintenance || !!Object.keys(errors).length}
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {isFetchingSingleMaintenance && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {/* <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="danger"
                      label={tMain("delete")}
                      onClick={() => { dispatch(setModalsData({ ...modalsState, showDeleteConfirmation: true })) }}
                      disabled={isLoadingEditMaintenance}
                    />
                  </div> */}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={userCanEdit ? tMain("update") : tMain("edit")}
                      onClick={userCanEdit ? handleEditMaintenance : () => setUserCanEdit(!userCanEdit)}
                      disabled={isFetchingSingleMaintenance || isLoadingEditMaintenance || (userCanEdit && !!Object.keys(errors).length)}//|| getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_MAINTENANCE) < ROLE_ACCESS_LEVELS.WRITE
                    /> */}
                    {MemoizedFormActionButtons}
                  </div>
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingSingleMaintenance || isLoadingEditMaintenance ? "opacity-40" : ""}`}
            >
              <form onSubmit={handleSubmit}>
                <Accordian
                  title={t("accord_odometer_details")}
                  openByDefault={true}
                >
                  <MaintenanceOdometerDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={
                      isFetchingSingleMaintenance || !formikValuesReady
                    }
                    distanceUnit={distanceUnits.toString()}
                    deviceid={deviceId}
                    setFaultCodeData={setFaultCodeData}
                    setIsLoading={setIsLoading}
                  />
                </Accordian>
                <Accordian title={t("accord_maintenance_interval_details")}>
                  <MaintenanceIntervalDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={
                      isFetchingSingleMaintenance || !formikValuesReady
                    }
                    distanceUnit={distanceUnits.toString()}
                    maintenanceFlag={maintenanceFlagInterval}
                  />
                </Accordian>
                <Accordian title={t("accord_last_maint_engine_hours")}>
                  <MaintenanceLastMaintEngineHoursDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={
                      isFetchingSingleMaintenance || !formikValuesReady
                    }
                    maintenanceFlag={maintenanceFlagLastMaintEngine}
                  />
                </Accordian>
                <Accordian title={t("accord_last_periodic_service_date")}>
                  <MaintenanceLastPerodicServiceDateForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={
                      isFetchingSingleMaintenance || !formikValuesReady
                    }
                    maintenanceFlag={maintenanceFlagLastPeriodicService}
                  />
                </Accordian>
              </form>
            </div>
          </div>
        </div>
      </div>

      <AddMaintenanceDetails />
      {modalsState?.showModalMaintenanceFaultCodes && !isLoading && (
        <ModalMaintenanceFaultCodes
          faultCodeData={faultCodeData}
          setFaultCodeData={setFaultCodeData}
        />
      )}
    </>
  );
};

export default ScreenAdminDetailMaintenance;
